import { CaretRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import pt from '../../locales/pt';
import {
  formatPotTargetLabel,
  getPurposeMeta,
  resolvePotOriginPath,
  resolvePotVaultIcon,
} from '../../lib/potPurposeUtils';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';

/**
 * @param {{ pot: object, purpose: import('../../lib/potPurposeUtils').PotPurpose }} props
 */
export function PotVaultRow({ pot, purpose }) {
  const navigate = useNavigate();
  const meta = getPurposeMeta(purpose);
  const Icon = resolvePotVaultIcon(pot, purpose);
  const accumulated = Number(pot.accumulated || 0);
  const target = Number(pot.target_amount || 0);
  const pct = Math.min(100, Math.max(0, Number(pot.progress_pct || 0)));
  const targetLabel = formatPotTargetLabel(pot);
  const origin = resolvePotOriginPath(pot);
  const canNavigate = Boolean(origin?.pathname);

  const handleOpenOrigin = () => {
    if (!origin?.pathname) return;
    navigate(origin.pathname, origin.state ? { state: origin.state } : undefined);
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleOpenOrigin}
        disabled={!canNavigate}
        aria-label={
          canNavigate
            ? `${pot.name}. ${targetLabel}. ${pt.pots.vaultRow.goToOrigin}`
            : `${pot.name}. ${targetLabel}`
        }
        className={cn(
          'flex w-full items-center gap-3 rounded-[0.9375rem] border border-border bg-card',
          'px-3.5 py-3 text-left transition-colors',
          canNavigate
            ? 'hover:bg-muted/40'
            : 'cursor-default opacity-95',
        )}
      >
        <span
          className={`flex h-[2.375rem] w-[2.375rem] shrink-0 items-center justify-center rounded-[0.6875rem] ${meta.iconBg}`}
        >
          <Icon className={`h-[1.125rem] w-[1.125rem] ${meta.iconColor}`} aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{pot.name}</p>
          <p className="mt-0.5 text-[10.5px] text-muted-foreground">{targetLabel}</p>
          <div className="mt-2 h-1.5 overflow-hidden rounded bg-muted">
            <span
              className={cn('block h-full rounded', meta.trackClass)}
              style={{ width: `${pct}%` }}
              aria-hidden
            />
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className="font-display text-sm font-bold tabular-nums text-foreground">
            {formatEUR(accumulated)}
          </p>
          <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">
            {pt.pots.vaultRow.ofTarget.replace('{amount}', formatEUR(target))}
          </p>
        </div>

        {canNavigate ? (
          <CaretRight
            className="h-[1.125rem] w-[1.125rem] shrink-0 text-stone-300 dark:text-stone-600"
            aria-hidden
          />
        ) : (
          <span className="w-[1.125rem] shrink-0" aria-hidden />
        )}
      </button>
    </li>
  );
}
